import {
  categoriesSelections,
  PlanWizardCategoryEnum,
} from '@chiroup/core/constants/SOAPPreferences';
import { CustomSelection } from '@chiroup/core/types/CustomSelection.type';
import { Visit, VisitPlanType } from '@chiroup/core/types/PatientVisit.type';
import React, { useState } from 'react';
import DiscoveryPopup from '../components/DiscoveryPopup';
import WalkthroughCallout from '../components/WalkthroughCallout';
import { DISCOVERY_COPY, OAP_COPY } from '../sandboxCopy';

interface PlanStepProps {
  visitForm: Visit;
  setVisitForm: React.Dispatch<React.SetStateAction<Visit>>;
  onAdvance: () => void;
  /** Returns to the last Objective beat (requirement 2026-09-28). */
  onBack: () => void;
}

type DiscoveryKey = 'imagingAndTests' | 'modalities' | 'rehab' | 'supplements' | 'treatmentDetails';

interface ChargeLine {
  name: string;
  cpt: string;
  units: number;
}

const EM_SELECTIONS = categoriesSelections[PlanWizardCategoryEnum.EvaluationManagement];
const TREATMENT_SELECTIONS = categoriesSelections[PlanWizardCategoryEnum.Treatments];

/** Stable string key for a selection (treatments use TreatmentKeys ids; E&M
 *  uses negative numeric ids — both stringify). */
const selectionKey = (selection: CustomSelection): string =>
  String(selection.id ?? selection.baseId);

/** Flatten the sandbox plan into billable lines (E&M + treatments). */
export const buildChargeLines = (plan: VisitPlanType | null | undefined): ChargeLine[] => {
  const lines: ChargeLine[] = [];

  Object.entries(plan?.evaluationAndManagement || {}).forEach(([id, entry]) => {
    const selection = EM_SELECTIONS.find(sel => selectionKey(sel) === String(id));
    if (!selection) return;
    lines.push({
      name: selection.name,
      cpt: entry?.services?.[0] || selection.primaryCptCode || '',
      units: entry?.units ?? 1,
    });
  });

  Object.values(plan?.treatments || {}).forEach(bySelection => {
    Object.entries(bySelection || {}).forEach(([id, entry]) => {
      const selection = TREATMENT_SELECTIONS.find(sel => selectionKey(sel) === String(id));
      if (!selection) return;
      lines.push({
        name: selection.name,
        cpt: entry?.services?.[0] || selection.primaryCptCode || '',
        units: entry?.units ?? 1,
      });
    });
  });

  return lines;
};

/**
 * Step 3 (Plan beat) — a controlled, sandbox-local rendition of the Plan
 * wizard: E&M and Treatments are open and functional (stock system-default
 * selections; CMT arrives auto-selected from the spinal segments documented
 * in Joints), the remaining categories are closed Discovery controls, and
 * Import Charges advances straight to Review & Sign.
 */
const PlanStep: React.FC<PlanStepProps> = ({ visitForm, setVisitForm, onAdvance, onBack }) => {
  const [discovery, setDiscovery] = useState<DiscoveryKey | null>(null);

  const copy = OAP_COPY.plan;
  const plan = visitForm.plan;

  const emSelectedId = Object.keys(plan?.evaluationAndManagement || {})[0];

  const toggleEm = (selection: CustomSelection) => {
    setVisitForm(prev => {
      const isSelected = String(emSelectedId) === selectionKey(selection);

      return {
        ...prev,
        plan: {
          ...prev.plan,
          evaluationAndManagement: isSelected
            ? {}
            : {
                [selectionKey(selection)]: {
                  units: 1,
                  services: selection.primaryCptCode ? [selection.primaryCptCode] : [],
                },
              },
        },
      };
    });
  };

  const isTreatmentSelected = (selection: CustomSelection): boolean =>
    !!(
      selection.subCategory && plan?.treatments?.[selection.subCategory]?.[selectionKey(selection)]
    );

  const setTreatment = (selection: CustomSelection, selected: boolean, units = 1) => {
    const subCategory = selection.subCategory;
    if (!subCategory) return;

    setVisitForm(prev => {
      const treatments = { ...(prev.plan?.treatments || {}) };
      // One selection per treatment subcategory (tester notes #20/#26): the
      // tiered options (CMT region counts, dry-needling muscle counts) are
      // mutually exclusive, matching how the Joints sync writes the CMT group.
      // This also guards the note builder, which repeats every entry in a
      // group once per key — two keys in one group render duplicated lines.
      const bySelection = selected ? {} : { ...(treatments[subCategory] || {}) };

      if (selected) {
        bySelection[selectionKey(selection)] = {
          ...bySelection[selectionKey(selection)],
          units,
          services: selection.primaryCptCode ? [selection.primaryCptCode] : [],
        };
        treatments[subCategory] = bySelection;
      } else {
        delete bySelection[selectionKey(selection)];

        if (Object.keys(bySelection).length) {
          treatments[subCategory] = bySelection;
        } else {
          delete treatments[subCategory];
        }
      }

      return { ...prev, plan: { ...prev.plan, treatments } };
    });
  };

  const treatmentUnits = (selection: CustomSelection): number =>
    (selection.subCategory &&
      plan?.treatments?.[selection.subCategory]?.[selectionKey(selection)]?.units) ||
    1;

  // One combined Plan beat (round-2 note #5) that imports and advances in a
  // single action — no confirmation receipt in between (round-3 note #2:
  // nothing should delay finishing the note). The charge count on the button
  // still shows what will import.
  const chargeCount = buildChargeLines(plan).length;

  const pill = (selected: boolean) =>
    `rounded-md border px-3 py-2 text-sm text-left transition-colors ${
      selected
        ? 'border-primary-600 bg-primary-600 text-white'
        : 'border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 text-gray-700 dark:text-gray-200 hover:border-primary-400'
    }`;

  const closedSection = (label: string, key: DiscoveryKey) => (
    <button
      type="button"
      onClick={() => setDiscovery(key)}
      className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-gray-700"
    >
      {label}
      <span className="text-gray-400">＋</span>
    </button>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
      <div className="flex-1 rounded-lg border border-gray-200 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 divide-y divide-gray-200 dark:divide-dark-gray-600">
        <div className="flex items-center justify-between px-4 py-3">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Plan</h2>
          <button
            type="button"
            onClick={onAdvance}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            Import Charges{chargeCount ? ` (${chargeCount})` : ''}
          </button>
        </div>

        <div className="px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Evaluation & Management
          </h3>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {EM_SELECTIONS.filter(sel => !sel.hideInPlan).map(sel => (
              <button
                key={selectionKey(sel)}
                type="button"
                className={pill(String(emSelectedId) === selectionKey(sel))}
                onClick={() => toggleEm(sel)}
              >
                {sel.name}
              </button>
            ))}
          </div>
        </div>

        {closedSection('Imaging & Tests', 'imagingAndTests')}

        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Treatments</h3>
            <button
              type="button"
              onClick={() => setDiscovery('treatmentDetails')}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              Detail
            </button>
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {TREATMENT_SELECTIONS.filter(sel => !sel.hideInPlan).map(sel => {
              const selected = isTreatmentSelected(sel);
              const units = treatmentUnits(sel);

              return (
                <div key={selectionKey(sel)} className="flex flex-col">
                  <button
                    type="button"
                    className={`${pill(selected)} flex-1`}
                    onClick={() => setTreatment(sel, !selected, units)}
                  >
                    {sel.name}
                  </button>
                  {selected && (sel.maxUnits || 1) > 1 && (
                    <div className="mt-1 flex items-center justify-end gap-2 text-xs text-gray-500 dark:text-gray-300">
                      <button
                        type="button"
                        aria-label={`Decrease ${sel.name} units`}
                        disabled={units <= 1}
                        onClick={() => setTreatment(sel, true, units - 1)}
                        className="h-5 w-5 rounded border border-gray-300 dark:border-dark-gray-600 leading-none disabled:opacity-40"
                      >
                        −
                      </button>
                      {units} unit{units === 1 ? '' : 's'}
                      <button
                        type="button"
                        aria-label={`Increase ${sel.name} units`}
                        disabled={units >= (sel.maxUnits || 1)}
                        onClick={() => setTreatment(sel, true, units + 1)}
                        className="h-5 w-5 rounded border border-gray-300 dark:border-dark-gray-600 leading-none disabled:opacity-40"
                      >
                        ＋
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {closedSection('Modalities', 'modalities')}
        {closedSection('Supplements & Supplies', 'supplements')}
        {closedSection('Rehab', 'rehab')}
      </div>

      <div className="lg:w-80 shrink-0">
        <WalkthroughCallout
          stepLabel={copy.stepLabel}
          title={copy.title}
          body={copy.body}
          cta={copy.cta}
          onCta={onAdvance}
          onBack={onBack}
          className="lg:sticky lg:top-4"
        />
      </div>

      {discovery === 'imagingAndTests' && (
        <DiscoveryPopup
          title={DISCOVERY_COPY.imagingAndTests.title}
          body={DISCOVERY_COPY.imagingAndTests.body}
          onDismiss={() => setDiscovery(null)}
        />
      )}
      {discovery === 'modalities' && (
        <DiscoveryPopup
          title={DISCOVERY_COPY.modalities.title}
          body={DISCOVERY_COPY.modalities.body}
          onDismiss={() => setDiscovery(null)}
        />
      )}
      {discovery === 'rehab' && (
        <DiscoveryPopup
          title={DISCOVERY_COPY.rehab.title}
          body={DISCOVERY_COPY.rehab.body}
          onDismiss={() => setDiscovery(null)}
        />
      )}
      {discovery === 'supplements' && (
        <DiscoveryPopup
          title={DISCOVERY_COPY.supplements.title}
          body={DISCOVERY_COPY.supplements.body}
          onDismiss={() => setDiscovery(null)}
        />
      )}
      {discovery === 'treatmentDetails' && (
        <DiscoveryPopup
          title={DISCOVERY_COPY.treatmentDetails.title}
          body={DISCOVERY_COPY.treatmentDetails.body}
          onDismiss={() => setDiscovery(null)}
        />
      )}
    </div>
  );
};

export default PlanStep;
